import { Injectable } from '@angular/core';
import { ChannelSheetCol, clearByChannelType, motorRunDefinitions } from '@models/channel-sheet';
import {
    runningHourDefaults,
    flowCounterDefaults,
    digitalDefaults,
    analogDefaults,
    digitalMotorDefaults,
    exhGasMeanDefaults,
    exhGasDevDefaults,
    LEnableDefaultsExhGasMean,
    MotorAlarmEnableDefaults,
    DigitalAlarmEnableDefaults,
    HHEnableDefaults,
    HEnableDefaults,
    LLEnableDefaults,
    LEnableDefaults,
    SensorFailEnableDefaults,
    FeedbackAlarmEnableDefaults,
    SystemAlarmEnableDefaults,
} from '@models/channel-sheet-defaults';

import {
    isRunningHourByChannelTypeString,
    isFlowCounterByChannelTypeString,
    isDigitalByChannelTypeString,
    isExhGasMeanByChannelTypeString,
    isBlank,
    ChannelTypeStrings,
    ChannelType,
    isRunType,
    isExhGasDevByChannelTypeString,
    isAnalogByChannelTypeString,
    isDigitalMotorByChannelTypeString,
    SimpleTypes,
    isDigitalWOMotorByChannelTypeString,
} from 'icreate/common/models/channel-type';
import { MotorType } from 'icreate/common/models/motor-alarm-setting';
import { getChannelStringFromSimpleType, getSimpleTypesFromString } from 'icreate/common/services/channel-object-utils';
import { CellValue } from 'jspreadsheet-ce';

@Injectable({
    providedIn: 'root',
})
export class ChannelSheetUtilsService {
    private mouseDownFunction;
    private mouseUpFunction;
    private cellCornerElement;
    private isFillingCells: boolean = false;
    private isAutoCellUpdateEvent: boolean = false;
    private selectedValue: CellValue;
    pendingCustomHistory: any;

    constructor() {
        this.mouseDownFunction = this.mouseDown.bind(this);
        this.mouseUpFunction = this.mouseUp.bind(this);
    }

    finalizeCustomHistory(worksheets: any) {
        if (!this.pendingCustomHistory?.length) {
            return;
        }
        const history = worksheets.history;

        if (!history || worksheets.historyIndex < 0) {
            return;
        }
        const currentAction = history[worksheets.historyIndex];

        if (!currentAction) {
            return;
        }

        currentAction.records = [...(currentAction.records || []), ...this.pendingCustomHistory];
        currentAction.custom = true;
        this.pendingCustomHistory = null;
    }

    private pushCustomHistory(records: any[]) {
        if (!records?.length) {
            return;
        }

        if (!this.pendingCustomHistory) {
            this.pendingCustomHistory = [];
        }

        this.pendingCustomHistory.push(...records);
    }

    bindMouseEvents(cornerElement, value) {
        if (!cornerElement) return;
        this.cellCornerElement = cornerElement;
        this.selectedValue = value;
        this.cellCornerElement.addEventListener('mousedown', this.mouseDownFunction);
        document.addEventListener('mouseup', this.mouseUpFunction);
    }

    removeMouseEvents() {
        if (!this.cellCornerElement) return;
        this.cellCornerElement.removeEventListener('mousedown', this.mouseDownFunction);
        document.removeEventListener('mouseup', this.mouseUpFunction);
        this.cellCornerElement = null;
        this.selectedValue = null;
    }

    private mouseDown() {
        this.isFillingCells = true;
    }

    private mouseUp() {
        this.isFillingCells = false;
    }

    isMoving() {
        return this.isFillingCells;
    }

    getIsAutoUpdate(): boolean {
        if (this.isAutoCellUpdateEvent) {
            this.isAutoCellUpdateEvent = false;
            return true;
        }
        return this.isAutoCellUpdateEvent;
    }

    private applyDefaults(
        worksheets: any,
        rowIndex: number,
        colIndex: number,
        newValue: string,
        oldValue: string,
        defaults: Partial<Record<ChannelSheetCol, string | null>>,
        channelType: string,
        isMotorTypeChange: boolean,
        inSimplewView?: boolean,
        clearedRecords: any[] = []
    ): void {
        worksheets.ignoreHistory = true;

        const row = Number(rowIndex);
        const oldStatus = worksheets.getValueFromCoords(ChannelSheetCol.STATUS, row);

        const changedRecords: any[] = [
            {
                newValue,
                oldValue,
                row,
                col: colIndex,
                y: row,
                x: colIndex,
            },
            ...clearedRecords,
        ];

        const defaultDrivenCols = [
            ChannelSheetCol.MEASURABLE_MAX,
            ChannelSheetCol.MEASURABLE_MIN,
            ChannelSheetCol.DIGITAL_INPUT_FILTER,
            ChannelSheetCol.UNIT,
            ChannelSheetCol.ALARM_RECOVERY_HYSTERESIS,
        ];

        if (!isMotorTypeChange) {
            for (const col of defaultDrivenCols) {
                const isSupportedInNewType = defaults && Object.prototype.hasOwnProperty.call(defaults, col);

                if (!isSupportedInNewType) {
                    const preValue = worksheets.getValueFromCoords(col, row);

                    const isOldValueADefault =
                        preValue === runningHourDefaults[col] ||
                        preValue === flowCounterDefaults[col] ||
                        preValue === digitalDefaults[col] ||
                        preValue === analogDefaults[col];

                    if (isOldValueADefault && preValue !== '' && preValue !== null) {
                        changedRecords.push({
                            newValue: '',
                            oldValue: preValue,
                            row,
                            col,
                            y: row,
                            x: col,
                        });
                    }
                }
            }
            const currentFcuNum = worksheets.getValueFromCoords(ChannelSheetCol.FCU_NO, row);

            if (!currentFcuNum) {
                changedRecords.push({
                    newValue: 1,
                    oldValue: currentFcuNum,
                    row,
                    col: ChannelSheetCol.FCU_NO,
                    y: row,
                    x: ChannelSheetCol.FCU_NO,
                });
            }

            if (inSimplewView) {
                changedRecords.push({
                    newValue: channelType,
                    oldValue: worksheets.getValueFromCoords(ChannelSheetCol.CHANNEL_TYPE, row),
                    row,
                    col: ChannelSheetCol.CHANNEL_TYPE,
                    y: row,
                    x: ChannelSheetCol.CHANNEL_TYPE,
                });

                const isDigitalEnabled = worksheets.getValueFromCoords(ChannelSheetCol.DIGITAL_ENABLE, row);
                if (isDigitalEnabled) {
                    const currentNormalOpen = worksheets.getValueFromCoords(ChannelSheetCol.NORMAL_OPEN, row);

                    if (currentNormalOpen && newValue === SimpleTypes.NC) {
                        changedRecords.push({
                            newValue: false,
                            oldValue: currentNormalOpen,
                            row,
                            col: ChannelSheetCol.NORMAL_OPEN,
                            y: row,
                            x: ChannelSheetCol.NORMAL_OPEN,
                        });
                    } else if (!currentNormalOpen && newValue === SimpleTypes.NO) {
                        changedRecords.push({
                            newValue: true,
                            oldValue: currentNormalOpen,
                            row,
                            col: ChannelSheetCol.NORMAL_OPEN,
                            y: row,
                            x: ChannelSheetCol.NORMAL_OPEN,
                        });
                    }
                }
            } else {
                const isNormalOpen = worksheets.getValueFromCoords(ChannelSheetCol.NORMAL_OPEN, row);
                const newChannelType = getSimpleTypesFromString(channelType, isNormalOpen);
                changedRecords.push({
                    newValue: newChannelType,
                    oldValue: worksheets.getValueFromCoords(ChannelSheetCol.SIMPLE_CHANNEL_TYPE, row),
                    row,
                    col: ChannelSheetCol.SIMPLE_CHANNEL_TYPE,
                    y: row,
                    x: ChannelSheetCol.SIMPLE_CHANNEL_TYPE,
                });
            }
        }

        if (defaults) {
            for (const [colKey, defaultValue] of Object.entries(defaults)) {
                const col = Number(colKey);
                const preValue = worksheets.getValueFromCoords(col, row);

                if (col === ChannelSheetCol.FCU_NO && preValue !== '') {
                    continue;
                }
                if (
                    isExhGasMeanByChannelTypeString(channelType) &&
                    (col === ChannelSheetCol.MEASURABLE_MAX || col === ChannelSheetCol.MEASURABLE_MIN) &&
                    preValue !== ''
                ) {
                    continue;
                }
                if (preValue === defaultValue) {
                    continue;
                }

                changedRecords.push({
                    newValue: defaultValue,
                    oldValue: preValue,
                    row,
                    col,
                    y: row,
                    x: col,
                });
            }
        }

        const root = worksheets.table;
        root?.style.setProperty('display', 'none');

        try {
            for (const record of changedRecords.slice(1)) {
                worksheets.setValueFromCoords(record.col, row, record.newValue, true);
            }

            const tempRow: any = {};

            for (const colId of [
                ...DIGITAL_STATUS_COLS,
                ...ANALOG_STATUS_COLS,
                ...SYSTEM_STATUS_COLS,
                ...MOTOR_STATUS_COLS,
            ]) {
                tempRow[colId] = worksheets.getValueFromCoords(colId, row);
            }

            const newStatus = this.calculateStatusString(tempRow, channelType);

            if (oldStatus !== newStatus) {
                changedRecords.push({
                    newValue: newStatus,
                    oldValue: oldStatus,
                    row,
                    col: ChannelSheetCol.STATUS,
                    y: row,
                    x: ChannelSheetCol.STATUS,
                });
                worksheets.setValueFromCoords(ChannelSheetCol.STATUS, row, newStatus, true);
            }
        } finally {
            worksheets.ignoreHistory = false;
            root?.style.removeProperty('display');
        }
        this.pushCustomHistory(changedRecords);
        this.isAutoCellUpdateEvent = true;
    }

    private applyBlanked(
        worksheets: any,
        rowIndex: number,
        oldValue: string,
        channelRows: any[],
        inSimpleView: boolean
    ): void {
        worksheets.ignoreHistory = true;

        const row = Number(rowIndex);
        const channelData = [...channelRows[rowIndex]];
        const changedRecords: any[] = [];
        const oldStatus = worksheets.getValueFromCoords(ChannelSheetCol.STATUS, row);
        if (oldStatus) {
            changedRecords.push({
                row,
                col: ChannelSheetCol.STATUS,
                y: row,
                x: ChannelSheetCol.STATUS,
                oldValue: oldStatus,
                newValue: '',
            });
        }

        for (let i = 1; i < channelData.length; i++) {
            const isTypeCol = i === ChannelSheetCol.CHANNEL_TYPE;
            const newData: any = {
                row,
                col: i,
                y: row,
                x: i,
                oldValue: isTypeCol ? oldValue : worksheets.getValueFromCoords(i, row),
                newValue: isTypeCol ? ChannelTypeStrings[ChannelType.BLANK] : null,
            };
            if (isTypeCol) {
                newData.oldValue = inSimpleView ? worksheets.getValueFromCoords(i, row) : oldValue;
                newData.newValue = ChannelTypeStrings[ChannelType.BLANK];
            } else if (i === ChannelSheetCol.SIMPLE_CHANNEL_TYPE) {
                newData.oldValue = inSimpleView ? oldValue : worksheets.getValueFromCoords(i, row);
                newData.newValue = SimpleTypes.Blank;
            }
            changedRecords.push(newData);
        }

        try {
            for (const record of changedRecords) {
                worksheets.setValueFromCoords(record.col, row, record.newValue, true);
            }
        } finally {
            worksheets.ignoreHistory = false;
        }
        this.pushCustomHistory(changedRecords);

        this.isAutoCellUpdateEvent = true;
    }

    handleChange(
        cell: any,
        colIndex: string | number,
        rowIndex: string | number,
        newValue: any,
        oldValue: any,
        worksheets: any,
        channelRows: any[],
        inSimplewView: boolean
    ) {
        if (worksheets.ignoreHistory) {
            return;
        }

        const numericColIndex = Number(colIndex);
        const numericRowIndex = Number(rowIndex);

        if (cell?.classList.contains('jexcel_dropdown') && !newValue) {
            this.revertDropdownValue(worksheets, numericColIndex, numericRowIndex, oldValue);
            return;
        }

        if (channelRows[numericRowIndex]) {
            channelRows[numericRowIndex][numericColIndex] = newValue;
        }

        if (
            numericColIndex === ChannelSheetCol.CHANNEL_TYPE ||
            numericColIndex === ChannelSheetCol.SIMPLE_CHANNEL_TYPE
        ) {
            this.handleChannelTypeChange(
                worksheets,
                numericRowIndex,
                numericColIndex,
                newValue,
                oldValue,
                channelRows,
                inSimplewView
            );
        } else if (numericColIndex === ChannelSheetCol.NORMAL_OPEN) {
            this.handleNormalOpenChange(worksheets, numericRowIndex, numericColIndex, newValue, oldValue);
        } else if (numericColIndex === ChannelSheetCol.MOTOR_TYPE) {
            this.handleMotorTypeChange(worksheets, numericRowIndex, numericColIndex, newValue, oldValue, channelRows);
        } else if (ALARM_COLUMNS.has(numericColIndex)) {
            this.handleAlarmEnableChange(worksheets, numericRowIndex, numericColIndex, newValue, oldValue, channelRows);
        }
        this.triggerStatusStringUpdate(worksheets, channelRows, numericColIndex, numericRowIndex, newValue);
    }

    private revertDropdownValue(worksheets: any, col: number, row: number, oldValue: any) {
        worksheets.ignoreHistory = true;
        try {
            worksheets.setValueFromCoords(col, row, oldValue, true);
        } finally {
            worksheets.ignoreHistory = false;
        }

        this.isAutoCellUpdateEvent = true;
    }

    private handleChannelTypeChange(
        worksheets: any,
        row: number,
        col: number,
        newValue: any,
        oldValue: any,
        channelRows: any[],
        inSimplewView: boolean
    ) {
        let channelType: string;

        if (inSimplewView) {
            const simpleType = worksheets.getValueFromCoords(ChannelSheetCol.SIMPLE_CHANNEL_TYPE, row);

            channelType = getChannelStringFromSimpleType(simpleType);
        } else {
            channelType = worksheets.getValueFromCoords(ChannelSheetCol.CHANNEL_TYPE, row);
        }

        const clearedRecords = clearByChannelType(channelRows[row], channelType, worksheets, row);

        if (isBlank(channelType)) {
            this.applyBlanked(worksheets, row, oldValue, channelRows, inSimplewView);
            return;
        }

        const defaults = this.getChannelTypeDefaults(channelType);
        this.applyDefaults(
            worksheets,
            row,
            col,
            newValue,
            oldValue,
            defaults,
            channelType,
            false,
            inSimplewView,
            clearedRecords
        );
        for (const [colKey, defaultValue] of Object.entries(defaults ?? {})) {
            channelRows[row][Number(colKey)] = defaultValue ?? '';
        }

        if (isDigitalMotorByChannelTypeString(channelType)) {
            this.handleMotorTypeChange(worksheets, row, col, newValue, oldValue, channelRows);
        }
    }

    private getChannelTypeDefaults(channelType: string) {
        if (isRunningHourByChannelTypeString(channelType)) {
            return runningHourDefaults;
        }
        if (isFlowCounterByChannelTypeString(channelType)) {
            return flowCounterDefaults;
        }
        if (isDigitalMotorByChannelTypeString(channelType)) {
            return digitalMotorDefaults;
        }
        if (isDigitalByChannelTypeString(channelType)) {
            return digitalDefaults;
        }
        if (isExhGasMeanByChannelTypeString(channelType)) {
            return exhGasMeanDefaults;
        }
        if (isExhGasDevByChannelTypeString(channelType)) {
            return exhGasDevDefaults;
        }
        if (isAnalogByChannelTypeString(channelType)) {
            return analogDefaults;
        }
        return null;
    }

    private handleNormalOpenChange(worksheets: any, row: number, col: number, newValue: any, oldValue: any) {
        const simpleType = worksheets.getValueFromCoords(ChannelSheetCol.SIMPLE_CHANNEL_TYPE, row);
        if (simpleType !== SimpleTypes.NO && simpleType !== SimpleTypes.NC) {
            return;
        }

        const newSimpleType = newValue ? SimpleTypes.NO : SimpleTypes.NC;
        const newSimpleTypeData = {
            row,
            col: ChannelSheetCol.SIMPLE_CHANNEL_TYPE,
            y: row,
            x: ChannelSheetCol.SIMPLE_CHANNEL_TYPE,
            oldValue: simpleType,
            newValue: newSimpleType,
        };

        const newNormalOpenData = {
            row,
            col,
            y: row,
            x: col,
            oldValue,
            newValue,
        };

        worksheets.ignoreHistory = true;

        try {
            worksheets.setValueFromCoords(col, row, newValue, true);
            worksheets.setValueFromCoords(ChannelSheetCol.SIMPLE_CHANNEL_TYPE, row, newSimpleType, true);
        } finally {
            worksheets.ignoreHistory = false;
        }

        this.pushCustomHistory([newNormalOpenData, newSimpleTypeData]);
        this.isAutoCellUpdateEvent = true;
    }

    private handleMotorTypeChange(
        worksheets: any,
        row: number,
        col: number,
        newValue: any,
        oldValue: any,
        channelRows: any[]
    ) {
        const motorType = channelRows[row][ChannelSheetCol.MOTOR_TYPE];
        const found = motorRunDefinitions.find(([type]) => isRunType(motorType, type));

        if (!found) {
            const cols = [
                ChannelSheetCol.STOP_STATUS,
                ChannelSheetCol.STBY_STATUS,
                ChannelSheetCol.RUN1_STATUS,
                ChannelSheetCol.RUN2_STATUS,
                ChannelSheetCol.RUN3_STATUS,
                ChannelSheetCol.RUN4_STATUS,
                ChannelSheetCol.RUN5_STATUS,
            ];
            const changedRecords: any[] = [
                {
                    newValue,
                    oldValue,
                    row,
                    col,
                    y: row,
                    x: col,
                },
            ];

            worksheets.ignoreHistory = true;
            try {
                for (const c of cols) {
                    const currentValue = worksheets.getValueFromCoords(c, row);

                    if (currentValue === '' || currentValue === null) {
                        continue;
                    }
                    changedRecords.push({
                        newValue: '',
                        oldValue: currentValue,
                        row,
                        col: c,
                        y: row,
                        x: c,
                    });

                    worksheets.setValueFromCoords(c, row, '', true);
                    channelRows[row][c] = '';
                }
            } finally {
                worksheets.ignoreHistory = false;
            }

            this.pushCustomHistory(changedRecords);
            this.isAutoCellUpdateEvent = true;

            return;
        }

        const defaults = found[1];
        this.applyDefaults(worksheets, row, col, newValue, oldValue, defaults, motorType, true);

        for (const [colKey, value] of Object.entries(defaults)) {
            channelRows[row][Number(colKey)] = value ?? '';
        }
    }

    private handleAlarmEnableChange(
        worksheets: any,
        row: number,
        col: number,
        newValue: any,
        oldValue: any,
        channelRows: any[]
    ) {
        const isFalseValue = newValue === false || newValue === 'FALSE' || newValue === 0;

        if (!newValue && !isFalseValue) {
            return;
        }
        if (isFalseValue) {
            let cascadeCol: number | null = null;
            if (col === ChannelSheetCol.H_ENABLE) {
                cascadeCol = ChannelSheetCol.HH_ENABLE;
            } else if (col === ChannelSheetCol.L_ENABLE) {
                cascadeCol = ChannelSheetCol.LL_ENABLE;
            }

            if (cascadeCol !== null) {
                const cascadeOldValue = worksheets.getValueFromCoords(cascadeCol, row);

                if (
                    cascadeOldValue !== false &&
                    cascadeOldValue !== '' &&
                    cascadeOldValue !== null &&
                    cascadeOldValue !== undefined
                ) {
                    const changedRecords = [
                        {
                            newValue,
                            oldValue,
                            row,
                            col,
                            y: row,
                            x: col,
                        },
                        {
                            newValue: false,
                            oldValue: cascadeOldValue,
                            row,
                            col: cascadeCol,
                            y: row,
                            x: cascadeCol,
                        },
                    ];

                    worksheets.ignoreHistory = true;

                    try {
                        worksheets.setValueFromCoords(cascadeCol, row, false, true);
                    } finally {
                        worksheets.ignoreHistory = false;
                    }

                    if (channelRows[row]) {
                        channelRows[row][cascadeCol] = false;
                    }

                    this.pushCustomHistory(changedRecords);

                    this.isAutoCellUpdateEvent = true;
                }

                return;
            }
            return;
        }
        this.applyDefaultAlarmStatus(worksheets, row, col, newValue, oldValue, channelRows);
    }

    private triggerStatusStringUpdate(worksheets: any, channelRows: any[], col: number, row: number, newValue: any) {
        const currentRow = channelRows[row];
        this.updateStatusString(currentRow, col, row, newValue, worksheets);
    }

    applyDefaultAlarmStatus(
        worksheets: any,
        rowIndex: number,
        colIndex: number,
        newValue: any,
        oldValue: any,
        channelRows: any[]
    ) {
        if (!newValue) return;

        let defaults =
            isExhGasMeanByChannelTypeString(channelRows[rowIndex][ChannelSheetCol.CHANNEL_TYPE]) &&
            colIndex === ChannelSheetCol.L_ENABLE
                ? LEnableDefaultsExhGasMean
                : ALARM_DEFAULTS_MAP[colIndex];

        if (colIndex === ChannelSheetCol.MOTOR_ENABLE) {
            const found = motorRunDefinitions.find(([type]) => isRunType(MotorType.RUN, type));

            if (found) {
                defaults = {
                    ...defaults,
                    ...found[1],
                };
            }
        }

        if (!defaults) return;

        const root = worksheets.table;

        root?.style.setProperty('display', 'none');
        worksheets.ignoreHistory = true;

        const changedRecords: any[] = [
            {
                newValue,
                oldValue,
                row: rowIndex,
                col: colIndex,
                y: rowIndex,
                x: colIndex,
            },
        ];

        try {
            for (const [colKey, defaultValue] of Object.entries(defaults)) {
                const currentCol = Number(colKey);
                const currentOldValue = worksheets.getValueFromCoords(currentCol, rowIndex);

                if (currentOldValue !== '' && currentOldValue !== null && currentOldValue !== undefined) continue;

                if (channelRows[rowIndex]) channelRows[rowIndex][currentCol] = defaultValue;

                changedRecords.push({
                    newValue: defaultValue,
                    oldValue: currentOldValue,
                    row: rowIndex,
                    col: currentCol,
                    y: rowIndex,
                    x: currentCol,
                });

                worksheets.setValueFromCoords(currentCol, rowIndex, defaultValue, true);
            }

            if (colIndex === ChannelSheetCol.DIGITAL_ENABLE) {
                const simpleChannelType = worksheets.getValueFromCoords(ChannelSheetCol.SIMPLE_CHANNEL_TYPE, rowIndex);
                const currentNormalOpen = worksheets.getValueFromCoords(ChannelSheetCol.NORMAL_OPEN, rowIndex);

                if (simpleChannelType === SimpleTypes.NC) {
                    changedRecords.push({
                        newValue: false,
                        oldValue: currentNormalOpen,
                        row: rowIndex,
                        col: ChannelSheetCol.NORMAL_OPEN,
                        y: rowIndex,
                        x: ChannelSheetCol.NORMAL_OPEN,
                    });
                    worksheets.setValueFromCoords(ChannelSheetCol.NORMAL_OPEN, rowIndex, false, true);

                    if (channelRows[rowIndex]) channelRows[rowIndex][ChannelSheetCol.NORMAL_OPEN] = false;
                }
            }
        } finally {
            worksheets.ignoreHistory = false;
            root?.style.removeProperty('display');
        }

        this.pushCustomHistory(changedRecords);
        this.isAutoCellUpdateEvent = true;
    }

    applyDefaultAlarmPanel(enbaled: boolean, channelType: ChannelType) {}

    handleBeforeChange(newValue) {
        if (this.isFillingCells && this.selectedValue) {
            const leadingZeros = (this.selectedValue as any).match(/^0+/)?.[0] ?? '';
            return leadingZeros + newValue;
        }
        return newValue;
    }

    private updateStatusString(currentRow: any[], colIndex: number, rowIndex: number, newValue: any, worksheets: any) {
        const row = Number(rowIndex);
        const col = Number(colIndex);

        const shouldUpdateStatus =
            DIGITAL_STATUS_COLS.includes(col) ||
            ANALOG_STATUS_COLS.includes(col) ||
            SYSTEM_STATUS_COLS.includes(col) ||
            MOTOR_STATUS_COLS.includes(col) ||
            col === ChannelSheetCol.CHANNEL_TYPE;

        if (!shouldUpdateStatus) {
            return;
        }

        const rowValues = [...currentRow];
        rowValues[col] = newValue;

        const channelType = rowValues[ChannelSheetCol.CHANNEL_TYPE];

        const oldStatus = worksheets.getValueFromCoords(ChannelSheetCol.STATUS, row);

        const newStatus = this.calculateStatusString(rowValues, channelType);

        if (oldStatus === newStatus) {
            return;
        }

        const statusRecord = {
            newValue: newStatus,
            oldValue: oldStatus,
            row,
            col: ChannelSheetCol.STATUS,
            y: row,
            x: ChannelSheetCol.STATUS,
        };

        worksheets.ignoreHistory = true;

        try {
            worksheets.setValueFromCoords(ChannelSheetCol.STATUS, row, newStatus, true);

            if (currentRow) {
                currentRow[ChannelSheetCol.STATUS] = newStatus;
            }
        } finally {
            worksheets.ignoreHistory = false;
        }
        this.pushCustomHistory([statusRecord]);
    }

    private calculateStatusString(rowVals: any, channelType: string): string {
        const getValue = (column: ChannelSheetCol) => rowVals[column];

        if (isDigitalMotorByChannelTypeString(channelType) && getValue(ChannelSheetCol.MOTOR_ENABLE)) {
            return getValue(ChannelSheetCol.MOTOR_TYPE);
        }

        if (isDigitalWOMotorByChannelTypeString(channelType)) {
            return `${getValue(ChannelSheetCol.DIGITAL_ON)}/${getValue(ChannelSheetCol.DIGITAL_OFF)}`;
        }

        if (
            isAnalogByChannelTypeString(channelType) ||
            isExhGasMeanByChannelTypeString(channelType) ||
            isExhGasDevByChannelTypeString(channelType)
        ) {
            const values: string[] = [];
            if (getValue(ChannelSheetCol.HH_ENABLE)) {
                values.push(getValue(ChannelSheetCol.HH_STRING));
            }
            if (getValue(ChannelSheetCol.H_ENABLE)) {
                values.push(getValue(ChannelSheetCol.H_STRING));
            }
            values.push('NOR');
            if (getValue(ChannelSheetCol.L_ENABLE)) {
                values.push(getValue(ChannelSheetCol.L_STRING));
            }
            if (getValue(ChannelSheetCol.LL_ENABLE)) {
                values.push(getValue(ChannelSheetCol.LL_STRING));
            }
            return values.join('/');
        }

        if (channelType?.startsWith('System')) {
            return SYSTEM_STATUS_COLS.map(column => getValue(column))
                .filter(Boolean)
                .join('/');
        }

        return '';
    }
}

const ALARM_COLUMNS = new Set([
    ChannelSheetCol.HH_ENABLE,
    ChannelSheetCol.H_ENABLE,
    ChannelSheetCol.LL_ENABLE,
    ChannelSheetCol.L_ENABLE,
    ChannelSheetCol.SENSOR_FAILURE_ENABLE,
    ChannelSheetCol.MOTOR_ENABLE,
    ChannelSheetCol.DIGITAL_ENABLE,
    ChannelSheetCol.FEEDBACK_ERROR_ENABLE,
    ChannelSheetCol.SYSTEM_ALARM_ENABLE,
]);

const ALARM_DEFAULTS_MAP: Record<number, Record<number, any>> = {
    [ChannelSheetCol.MOTOR_ENABLE]: MotorAlarmEnableDefaults,
    [ChannelSheetCol.DIGITAL_ENABLE]: DigitalAlarmEnableDefaults,
    [ChannelSheetCol.HH_ENABLE]: HHEnableDefaults,
    [ChannelSheetCol.H_ENABLE]: HEnableDefaults,
    [ChannelSheetCol.LL_ENABLE]: LLEnableDefaults,
    [ChannelSheetCol.L_ENABLE]: LEnableDefaults,
    [ChannelSheetCol.SENSOR_FAILURE_ENABLE]: SensorFailEnableDefaults,
    [ChannelSheetCol.FEEDBACK_ERROR_ENABLE]: FeedbackAlarmEnableDefaults,
    [ChannelSheetCol.SYSTEM_ALARM_ENABLE]: SystemAlarmEnableDefaults,
};

const DIGITAL_STATUS_COLS = [ChannelSheetCol.DIGITAL_ENABLE, ChannelSheetCol.DIGITAL_ON, ChannelSheetCol.DIGITAL_OFF];

const MOTOR_STATUS_COLS = [ChannelSheetCol.MOTOR_ENABLE, ChannelSheetCol.MOTOR_TYPE];

const ANALOG_STATUS_COLS = [
    ChannelSheetCol.HH_ENABLE,
    ChannelSheetCol.H_ENABLE,
    ChannelSheetCol.L_ENABLE,
    ChannelSheetCol.LL_ENABLE,
    ChannelSheetCol.HH_STRING,
    ChannelSheetCol.H_STRING,
    ChannelSheetCol.L_STRING,
    ChannelSheetCol.LL_STRING,
];

const SYSTEM_STATUS_COLS = [
    ChannelSheetCol.SYSTEM_ALARM_STRING1,
    ChannelSheetCol.SYSTEM_ALARM_STRING2,
    ChannelSheetCol.SYSTEM_ALARM_STRING3,
    ChannelSheetCol.SYSTEM_ALARM_STRING4,
    ChannelSheetCol.SYSTEM_ALARM_STRING5,
    ChannelSheetCol.SYSTEM_ALARM_STRING6,
    ChannelSheetCol.SYSTEM_ALARM_STRING7,
    ChannelSheetCol.SYSTEM_ALARM_STRING8,
    ChannelSheetCol.SYSTEM_ALARM_STRING9,
    ChannelSheetCol.SYSTEM_ALARM_STRING10,
    ChannelSheetCol.SYSTEM_ALARM_STRING11,
    ChannelSheetCol.SYSTEM_ALARM_STRING12,
    ChannelSheetCol.SYSTEM_ALARM_STRING13,
    ChannelSheetCol.SYSTEM_ALARM_STRING14,
    ChannelSheetCol.SYSTEM_ALARM_STRING15,
    ChannelSheetCol.SYSTEM_ALARM_STRING16,
];
